/**
 * University Review Wizard - Alpine.js Component
 * Full-page wizard for creating/editing university reviews
 */

import { wizardComponent } from '../../../../shared/components/wizard.js';

// Default review settings
const defaultReviewSettings = {
    'courses_point_text': { '1': 'چنگی به دل نمیزنه!', '2': 'بدک نیست!', '3': 'معمولیه', '4': 'بهترین نیست ولی خیلی خوبه!', '5': 'بهتر از اینم مگه میشه؟!' },
    'research_credit_point_text': { '1': 'چنگی به دل نمیزنه!', '2': 'بدک نیست!', '3': 'معمولیه', '4': 'بهترین نیست ولی خیلی خوبه!', '5': 'بهتر از اینم مگه میشه؟!' },
    'professor_academic_point_text': { '1': 'استاد خفن نایاب و کمیابه!', '2': 'استاد خوب داره، ولی کمه، کم!', '3': 'مجموع تیمیش متعادله!', '4': 'استاد خوب زیاد داره', '5': 'بهترین اساتید کل جهان!' },
    'professor_ethics_point_text': { '1': 'خیلی بده', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'course_selection_experience_point_text': { '1': 'خیلی بده', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'accountability_point_text': { '1': 'کار اداری پیش میاد، گریه ام میگیره!', '2': 'کار راه میفته، ولی خیلی سخت!', '3': 'نه تند، نه کند!', '4': 'خدا خیرشون بده!', '5': 'اشاره کنی، کارت راه میفته!' },
    'school_bus_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'public_transportation_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'placement_point_text': { '1': 'جت شخصی هم جواب نمیده!', '2': 'بالاخره میرسی، ناامید نشو!', '3': 'معقوله!', '4': 'راحت میرسی!', '5': 'اصلا نفهمیدم چجوری رسیدم!' },
    'library_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'self_service_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'buffet_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'green_space_point_text': { '1': 'اصلاً نداره', '2': 'کافی نیست', '3': 'معمولیه', '4': 'خوبه', '5': 'کافی و مناسبه' },
    'parking_point_text': { '1': 'اصلاً نداره', '2': 'کافی نیست', '3': 'معمولیه', '4': 'خوبه', '5': 'کافی و مناسبه' },
    'internet_point_text': { '1': 'اصلاً نداره', '2': 'داره اما کُنده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' },
    'sport_appliances_point_text': { '1': 'اصلاً نداره', '2': 'بده', '3': 'معمولیه', '4': 'خوبه', '5': 'عالیه' }
};

export default function universityReviewWizard(config = {}) {
    const baseWizard = wizardComponent({
        totalSteps: 6,
        currentStep: 1,
        draftKey: config.draftKey || `university_review_${config.universityId}`,
        autoSaveInterval: 30000
    });

    // Merge review settings
    const mergedReviewSettings = { ...defaultReviewSettings };
    if (config.reviewSettings && typeof config.reviewSettings === 'object') {
        Object.keys(config.reviewSettings).forEach(key => {
            if (config.reviewSettings[key] && typeof config.reviewSettings[key] === 'object') {
                mergedReviewSettings[key] = { ...defaultReviewSettings[key], ...config.reviewSettings[key] };
            } else if (config.reviewSettings[key]) {
                mergedReviewSettings[key] = config.reviewSettings[key];
            }
        });
    }

    return {
        ...baseWizard,

        // Configuration
        universityId: config.universityId || null,
        university: config.university || null,
        existingReview: config.existingReview || null,
        reviewSettings: mergedReviewSettings,

        // All 16 Rating Fields (discrete values 1-5)
        courses_point: 1,
        research_credit_point: 1,
        professor_academic_point: 1,
        professor_ethics_point: 1,
        course_selection_experience_point: 1,
        accountability_point: 1,
        school_bus_point: 1,
        public_transportation_point: 1,
        placement_point: 1,
        library_point: 1,
        self_service_point: 1,
        buffet_point: 1,
        green_space_point: 1,
        parking_point: 1,
        internet_point: 1,
        sport_appliances_point: 1,

        // Basic Info Fields
        educational_field: '',
        educational_field_id: null,
        educational_field_text: '',
        entrance_year: '',
        comment: '',

        // UI State
        commentTyped: false,
        liveRate: 1.0,

        // Dropdowns
        educationalFields: [],
        fieldSearchQuery: '',
        showFieldDropdown: false,

        // Override init
        init() {
            // Call base wizard init
            baseWizard.init.call(this);

            // Ensure universityId is set from config
            if (!this.universityId && config.universityId) {
                this.universityId = config.universityId;
            }

            // Initialize form data
            this.formData = this.getFormData();
            this.updateLiveRate();

            // Load existing review if editing
            if (this.existingReview) {
                this.loadExistingReview();
            } else if (this.universityId) {
                this.loadUserReview();
            }

            // Load educational fields
            this.loadEducationalFields();

            // Watch for rating changes
            this.$watch('courses_point', () => this.updateLiveRate());
            this.$watch('research_credit_point', () => this.updateLiveRate());
            this.$watch('professor_academic_point', () => this.updateLiveRate());
            this.$watch('professor_ethics_point', () => this.updateLiveRate());
            this.$watch('course_selection_experience_point', () => this.updateLiveRate());
            this.$watch('accountability_point', () => this.updateLiveRate());
            this.$watch('school_bus_point', () => this.updateLiveRate());
            this.$watch('public_transportation_point', () => this.updateLiveRate());
            this.$watch('placement_point', () => this.updateLiveRate());
            this.$watch('library_point', () => this.updateLiveRate());
            this.$watch('self_service_point', () => this.updateLiveRate());
            this.$watch('buffet_point', () => this.updateLiveRate());
            this.$watch('green_space_point', () => this.updateLiveRate());
            this.$watch('parking_point', () => this.updateLiveRate());
            this.$watch('internet_point', () => this.updateLiveRate());
            this.$watch('sport_appliances_point', () => this.updateLiveRate());

            // Hide suggestions when user types
            this.$nextTick(() => {
                const commentTextarea = this.$refs.commentTextarea;
                if (commentTextarea) {
                    commentTextarea.addEventListener('input', () => {
                        this.commentTyped = commentTextarea.value.length > 0;
                    });
                }
            });

            // Initialize emoji picker after delay
            setTimeout(() => {
                this.initEmojiPicker();
            }, 200);
        },

        // Get form data for draft saving
        getFormData() {
            return {
                courses_point: this.courses_point,
                research_credit_point: this.research_credit_point,
                professor_academic_point: this.professor_academic_point,
                professor_ethics_point: this.professor_ethics_point,
                course_selection_experience_point: this.course_selection_experience_point,
                accountability_point: this.accountability_point,
                school_bus_point: this.school_bus_point,
                public_transportation_point: this.public_transportation_point,
                placement_point: this.placement_point,
                library_point: this.library_point,
                self_service_point: this.self_service_point,
                buffet_point: this.buffet_point,
                green_space_point: this.green_space_point,
                parking_point: this.parking_point,
                internet_point: this.internet_point,
                sport_appliances_point: this.sport_appliances_point,
                educational_field_id: this.educational_field_id,
                educational_field_text: this.educational_field_text,
                educational_field: this.educational_field,
                entrance_year: this.entrance_year,
                comment: this.comment
            };
        },

        // Validate step
        validateStep(step) {
            switch(step) {
                case 1:
                    // Comment is optional, no validation needed
                    return true;
                case 2:
                    // Education Quality (2 fields)
                    if (this.courses_point < 1 || this.courses_point > 5 ||
                        this.research_credit_point < 1 || this.research_credit_point > 5) {
                        this.setErrors({ ratings: ['لطفاً تمام امتیازها را وارد کنید'] });
                        return false;
                    }
                    return true;
                case 3:
                    // Facilities (7 fields)
                    const facilityFields = [
                        'library_point', 'self_service_point', 'buffet_point',
                        'green_space_point', 'parking_point', 'internet_point', 'sport_appliances_point'
                    ];
                    for (const field of facilityFields) {
                        if (this[field] < 1 || this[field] > 5) {
                            this.setErrors({ ratings: ['لطفاً تمام امتیازها را وارد کنید'] });
                            return false;
                        }
                    }
                    return true;
                case 4:
                    // Academic Staff (2 fields)
                    if (this.professor_academic_point < 1 || this.professor_academic_point > 5 ||
                        this.professor_ethics_point < 1 || this.professor_ethics_point > 5) {
                        this.setErrors({ ratings: ['لطفاً تمام امتیازها را وارد کنید'] });
                        return false;
                    }
                    return true;
                case 5:
                    // Administrative & Accessibility (5 fields)
                    if (this.course_selection_experience_point < 1 || this.course_selection_experience_point > 5 ||
                        this.accountability_point < 1 || this.accountability_point > 5 ||
                        this.school_bus_point < 1 || this.school_bus_point > 5 ||
                        this.public_transportation_point < 1 || this.public_transportation_point > 5 ||
                        this.placement_point < 1 || this.placement_point > 5) {
                        this.setErrors({ ratings: ['لطفاً تمام امتیازها را وارد کنید'] });
                        return false;
                    }
                    return true;
                case 6:
                    // Educational Info
                    if (!this.educational_field_id) {
                        this.setErrors({ educational_field_id: ['لطفاً رشته تحصیلی را انتخاب کنید'] });
                        return false;
                    }
                    if (!this.entrance_year || !/^[0-9۰-۹]+$/.test(this.entrance_year)) {
                        this.setErrors({ entrance_year: ['لطفاً سال ورود معتبر وارد کنید'] });
                        return false;
                    }
                    return true;
                default:
                    return true;
            }
        },

        // Calculate live rate (average of all 16 points)
        updateLiveRate() {
            const points = [
                Math.round(this.courses_point),
                Math.round(this.research_credit_point),
                Math.round(this.professor_academic_point),
                Math.round(this.professor_ethics_point),
                Math.round(this.course_selection_experience_point),
                Math.round(this.accountability_point),
                Math.round(this.school_bus_point),
                Math.round(this.public_transportation_point),
                Math.round(this.placement_point),
                Math.round(this.library_point),
                Math.round(this.self_service_point),
                Math.round(this.buffet_point),
                Math.round(this.green_space_point),
                Math.round(this.parking_point),
                Math.round(this.internet_point),
                Math.round(this.sport_appliances_point)
            ];

            const sum = points.reduce((acc, val) => acc + val, 0);
            this.liveRate = sum / 16;
            this.liveRate = parseFloat(this.liveRate.toFixed(1));
        },

        // Get description for rating
        getDescription(key, value) {
            const roundedValue = Math.round(value);
            const settingKey = key.replace('_point', '_point_text');
            const descriptions = this.reviewSettings[settingKey];
            if (descriptions && descriptions[String(roundedValue)]) {
                return descriptions[String(roundedValue)];
            }
            return 'متوسط';
        },

        // Load existing review data
        loadExistingReview() {
            if (!this.existingReview) return;

            this.courses_point = Math.round(parseFloat(this.existingReview.courses_point) || 1);
            this.research_credit_point = Math.round(parseFloat(this.existingReview.research_credit_point) || 1);
            this.professor_academic_point = Math.round(parseFloat(this.existingReview.professor_academic_point) || 1);
            this.professor_ethics_point = Math.round(parseFloat(this.existingReview.professor_ethics_point) || 1);
            this.course_selection_experience_point = Math.round(parseFloat(this.existingReview.course_selection_experience_point) || 1);
            this.accountability_point = Math.round(parseFloat(this.existingReview.accountability_point) || 1);
            this.school_bus_point = Math.round(parseFloat(this.existingReview.school_bus_point) || 1);
            this.public_transportation_point = Math.round(parseFloat(this.existingReview.public_transportation_point) || 1);
            this.placement_point = Math.round(parseFloat(this.existingReview.placement_point) || 1);
            this.library_point = Math.round(parseFloat(this.existingReview.library_point) || 1);
            this.self_service_point = Math.round(parseFloat(this.existingReview.self_service_point) || 1);
            this.buffet_point = Math.round(parseFloat(this.existingReview.buffet_point) || 1);
            this.green_space_point = Math.round(parseFloat(this.existingReview.green_space_point) || 1);
            this.parking_point = Math.round(parseFloat(this.existingReview.parking_point) || 1);
            this.internet_point = Math.round(parseFloat(this.existingReview.internet_point) || 1);
            this.sport_appliances_point = Math.round(parseFloat(this.existingReview.sport_appliances_point) || 1);

            this.educational_field_id = this.existingReview.educationalField?.id || null;
            this.educational_field_text = this.existingReview.educationalField?.title || '';
            this.educational_field = this.educational_field_id ? String(this.educational_field_id) : '';
            this.entrance_year = this.existingReview.entrance_year || '';
            this.comment = this.existingReview.comment || '';

            this.updateLiveRate();
        },

        // Load user's existing review
        async loadUserReview() {
            if (!this.universityId) return;

            try {
                const response = await fetch(`/universities/${this.universityId}/review/user`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.review) {
                        this.existingReview = data.review;
                        this.loadExistingReview();
                    }
                }
            } catch (error) {
                console.warn('Failed to load user review:', error);
            }
        },

        // Load educational fields
        async loadEducationalFields() {
            try {
                const response = await fetch('/api/fields/dynamic', {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    this.educationalFields = data.fields || [];
                } else {
                    const fallbackResponse = await fetch('/api/fields', {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' }
                    });
                    if (fallbackResponse.ok) {
                        const fallbackData = await fallbackResponse.json();
                        if (fallbackData.data && Array.isArray(fallbackData.data)) {
                            this.educationalFields = fallbackData.data.map(item => ({
                                id: item.id,
                                title: item.title || item.name
                            }));
                        }
                    }
                }
            } catch (error) {
                console.warn('Failed to load educational fields:', error);
            }
        },

        // Search educational fields
        async searchEducationalFields() {
            if (!this.fieldSearchQuery || this.fieldSearchQuery.length < 2) {
                await this.loadEducationalFields();
                return;
            }

            try {
                const response = await fetch(`/api/fields/search?q=${encodeURIComponent(this.fieldSearchQuery)}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.data && Array.isArray(data.data)) {
                        this.educationalFields = data.data.map(item => ({
                            id: item.id,
                            title: item.title || item.name
                        }));
                    } else if (data.fields && Array.isArray(data.fields)) {
                        this.educationalFields = data.fields;
                    } else if (Array.isArray(data)) {
                        this.educationalFields = data;
                    }
                }
            } catch (error) {
                console.warn('Failed to search educational fields:', error);
            }
        },

        // Select educational field
        selectField(field) {
            this.educational_field_id = field.id;
            this.educational_field_text = field.title || field.name;
            this.educational_field = String(field.id);
            this.showFieldDropdown = false;
            this.fieldSearchQuery = '';
        },

        // Insert suggestion
        insertSuggestion(suggestion) {
            this.comment = String(suggestion);
            this.commentTyped = true;
            this.$refs.commentTextarea?.focus();
        },

        // Override submit
        async onSubmit(formData) {
            // Prepare form data
            const submitData = {
                review_id: this.existingReview?.id || null,
                educational_field: this.educational_field_id ? String(this.educational_field_id) : null,
                entrance_year: this.entrance_year,
                courses_point: this.courses_point,
                research_credit_point: this.research_credit_point,
                professor_academic_point: this.professor_academic_point,
                professor_ethics_point: this.professor_ethics_point,
                course_selection_experience_point: this.course_selection_experience_point,
                accountability_point: this.accountability_point,
                school_bus_point: this.school_bus_point,
                public_transportation_point: this.public_transportation_point,
                placement_point: this.placement_point,
                library_point: this.library_point,
                self_service_point: this.self_service_point,
                buffet_point: this.buffet_point,
                green_space_point: this.green_space_point,
                parking_point: this.parking_point,
                internet_point: this.internet_point,
                sport_appliances_point: this.sport_appliances_point,
                comment: this.comment
            };

            // Remove null/empty values
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === null || submitData[key] === '') {
                    delete submitData[key];
                }
            });

            // Determine endpoint
            const endpoint = this.existingReview?.id 
                ? '/universities/review/update'
                : `/universities/${this.universityId}/review`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.getCsrfToken()
                },
                credentials: 'same-origin',
                body: JSON.stringify(submitData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                this.showSuccessMessage(data.message || (this.existingReview?.id ? 'نظر شما با موفقیت ویرایش شد.' : 'نظر شما با موفقیت ثبت شد.'));

                // Clear intended URL from sessionStorage to prevent redirect to homepage
                sessionStorage.removeItem('intendedUrl');
                sessionStorage.removeItem('pendingReviewUniversityId');

                window.dispatchEvent(new CustomEvent('review-submitted', {
                    detail: {
                        success: true,
                        message: this.successMessage,
                        review_id: data.review_id
                    }
                }));

                // Redirect after delay
                setTimeout(() => {
                    window.location.href = `/universities/${this.universityId}`;
                }, 2000);
            } else {
                if (data.errors) {
                    this.setErrors(data.errors);
                } else {
                    this.setErrors({ general: [data.message || 'خطا در ثبت نظر. لطفاً دوباره تلاش کنید.'] });
                }
                throw new Error(data.message || 'Submission failed');
            }
        },

        // Get CSRF token
        getCsrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        },

        // Initialize emoji picker
        initEmojiPicker() {
            if (typeof EmojiPicker !== 'undefined' && typeof jQuery !== 'undefined') {
                try {
                    if (!window.emojiPicker) {
                        window.emojiPicker = new EmojiPicker({
                            emojiable_selector: "[data-emojiable=true]",
                            assetsPath: "/assets/emoji-picker/img/",
                            popupButtonClasses: "fa fa-smile-o"
                        });
                    }
                    window.emojiPicker.discover();
                } catch (e) {
                    console.warn('Emoji picker initialization failed:', e);
                }
            }
        }
    };
}

